import { motion } from 'framer-motion';
import { Droplets } from 'lucide-react';

const FlavorMenu = () => {
  const flavors = [
    {
      name: 'White Cherry',
      description: 'Sweet and smooth with a delicate cherry finish',
      color: '#FFB6C1',
      image: 'https://customer-assets.emergentagent.com/job_slushd-refresh/artifacts/l3s6bbfm_White%20Cherry.png',
    },
    {
      name: 'Arctic Berry',
      description: 'A cool blast of mixed berries with an icy kick',
      color: '#4169E1',
      image: 'https://customer-assets.emergentagent.com/job_slushd-refresh/artifacts/6cbliyv8_Arctic%20Berry.png',
    },
    {
      name: 'Strawberry',
      description: 'Classic strawberry sweetness in every sip',
      color: '#FF4757',
      image: 'https://customer-assets.emergentagent.com/job_slushd-refresh/artifacts/lq5gjc76_Strawberry.png',
    },
    {
      name: 'Peach Mango',
      description: 'Tropical fusion of ripe peach and sweet mango',
      color: '#FFA500',
      image: 'https://customer-assets.emergentagent.com/job_slushd-refresh/artifacts/qpxfdnsy_Peach%20Mango.png',
    },
    {
      name: 'Green Watermelon',
      description: 'Refreshing watermelon with a vibrant green twist',
      color: '#00FF7F',
      image: 'https://customer-assets.emergentagent.com/job_slushd-refresh/artifacts/v048ocgj_Green%20Watermelon.png',
    },
    {
      name: 'Strawberry Lemonade',
      description: 'Tangy lemonade meets sweet strawberry perfection',
      color: '#FF6B81',
      image: 'https://customer-assets.emergentagent.com/job_slushd-refresh/artifacts/yyw92x7i_Strawberry%20Lemonade.png',
    },
    {
      name: 'Strawberry Kiwi',
      description: 'A perfect balance of strawberry and tropical kiwi',
      color: '#7CFC00',
      image: 'https://customer-assets.emergentagent.com/job_slushd-refresh/artifacts/x3knvxkd_Strawberry%20Kiwi.png',
    },
    {
      name: 'Sour Apple',
      description: 'Bold and tangy with a punch of green apple',
      color: '#ADFF2F',
      image: 'https://customer-assets.emergentagent.com/job_slushd-refresh/artifacts/n55vaymi_Sour%20Apple.png',
    },
    {
      name: 'French Vanilla Cappuccino',
      description: 'Creamy frozen coffee with rich vanilla flavor',
      color: '#D2B48C',
      image: 'https://customer-assets.emergentagent.com/job_slushd-refresh/artifacts/io168rzu_French%20Vanilla%20Frozen%20Cappuccino.png',
    },
  ];

  return (
    <section 
      id="flavors" 
      data-testid="flavors-section"
      className="relative py-24 md:py-32 bg-[#0a0a0a] noise-bg"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#FF0099] text-sm font-medium tracking-widest uppercase mb-4 block">
            Our Flavors
          </span>
          <h2 className="text-4xl md:text-6xl font-bold tracking-tight uppercase mb-6">
            <span className="text-white">PURE</span>{' '}
            <span className="text-gradient">REFRESHMENT</span>
          </h2>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            100% juice-based flavors with no added sugar or high-fructose corn syrup. 
            Enriched with vitamins A, C, D and electrolytes for sustained energy.
          </p>
        </motion.div>

        {/* Flavors Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {flavors.map((flavor, index) => (
            <motion.div
              key={flavor.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group glass-card rounded-2xl overflow-hidden transition-all duration-300 hover:border-[var(--flavor-color)]/50"
              style={{ '--flavor-color': flavor.color }}
              data-testid={`flavor-card-${index}`}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                  style={{ backgroundImage: `url(${flavor.image})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                
                {/* Color indicator */}
                <div 
                  className="absolute top-4 right-4 w-4 h-4 rounded-full"
                  style={{ 
                    backgroundColor: flavor.color,
                    boxShadow: `0 0 20px ${flavor.color}`,
                  }}
                />
              </div>
              
              {/* Content */}
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Droplets 
                    className="w-5 h-5" 
                    style={{ color: flavor.color }}
                  />
                  <h3 className="text-xl font-bold text-white uppercase tracking-wide">
                    {flavor.name}
                  </h3>
                </div>
                <p className="text-white/60 text-sm leading-relaxed">
                  {flavor.description}
                </p>
              </div>
              
              {/* Bottom Glow Effect on Hover */}
              <div 
                className="h-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ 
                  background: `linear-gradient(to right, transparent, ${flavor.color}, transparent)`,
                }}
              />
            </motion.div>
          ))}
        </div>

        {/* Custom Flavors Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 text-center space-y-4"
        >
          <div className="flex flex-wrap justify-center gap-3">
            {['Vegan', 'Gluten Free', 'No Added Sugar', 'No HFCS'].map((tag) => (
              <span key={tag} className="px-4 py-1.5 rounded-full text-xs font-medium uppercase tracking-wider bg-[#FF0099]/10 text-[#FF0099] border border-[#FF0099]/30">
                {tag}
              </span>
            ))}
          </div>
          <p className="text-white/50 text-sm uppercase tracking-widest">
            Custom flavors available for special events. Contact us for details.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default FlavorMenu;
